import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { api } from "../lib/api";
import { COPY } from "../shared/copy";

type Phase = "compose" | "working" | "followup" | "failed";
type SeasonState = "loading" | "ready" | "none" | "over";

// Single-screen guided check-in: prompts stay visible, one text box, type your
// update (tap the phone keyboard mic to talk if you like). At most one AI
// follow-up. Transcript stays private to the owner.
export function CheckIn() {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const [seasonId, setSeasonId] = useState<string | null>(null);
  // The screen used to paint a compose box before it knew whether there was a
  // season to attach an update to. With no season, "Send it" hit a silent return
  // and the button just did nothing, forever.
  const [seasonState, setSeasonState] = useState<SeasonState>("loading");
  const [phase, setPhase] = useState<Phase>("compose");
  const [text, setText] = useState("");
  const [checkInId, setCheckInId] = useState<string | null>(null);
  const [followupPrompt, setFollowupPrompt] = useState("");
  const [followupText, setFollowupText] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    supabase
      .from("seasons")
      .select("id, status")
      .eq("league_id", leagueId)
      .maybeSingle()
      .then(({ data }) => {
        if (!alive) return;
        setSeasonId(data?.id ?? null);
        // Draft and active both accept updates: a draft season is exactly when
        // the league needs inventory most. A finished one cannot use them.
        if (!data?.id) setSeasonState("none");
        else if (data.status === "complete") setSeasonState("over");
        else setSeasonState("ready");
      });
    return () => { alive = false; };
  }, [leagueId]);

  // Two very different failures, told apart for the member: the question builder
  // being down is not their problem to solve, while an update with nothing
  // concrete in it is something they can fix by adding a detail.
  function describeFailure(res: any): string {
    if (res?.status === "provider_error") {
      return "Our question builder is having a problem on our end, not yours. Nothing you wrote was lost. Try again in a few minutes, and tell Brian if it keeps happening.";
    }
    return "We couldn't turn that into questions yet. Add a specific detail or two, like what changed, when, or who was there, then send it again.";
  }

  async function submitInitial() {
    if (!seasonId || !text.trim()) return;
    setPhase("working");
    setErr("");
    try {
      const res = await api<any>(`/api/checkin/submit`, {
        body: { op: "create", seasonId, leagueId, text, inputType: "text" },
      });
      if (res.status === "followup") {
        setCheckInId(res.checkInId);
        setFollowupPrompt(res.followupPrompt);
        setPhase("followup");
      } else if (res.status === "ready") {
        navigate(`/league/${leagueId}/candidates`);
      } else {
        setErr(describeFailure(res));
        setPhase("failed");
      }
    } catch (e: any) {
      setErr(
        e?.message?.includes("fetch")
          ? "Couldn't reach us. Check your connection and send it again."
          : "That didn't go through. Nothing you wrote was lost. Try again."
      );
      setPhase("failed");
    }
  }

  async function submitFollowup() {
    setPhase("working");
    setErr("");
    try {
      const res = await api<any>(`/api/checkin/submit`, { body: { op: "followup", checkInId, text: followupText } });
      if (res.status === "ready") {
        navigate(`/league/${leagueId}/candidates`);
      } else {
        setErr(describeFailure(res));
        setPhase("failed");
      }
    } catch (e: any) {
      setErr(
        e?.message?.includes("fetch")
          ? "Couldn't reach us. Check your connection and try again."
          : "That didn't go through. Nothing you wrote was lost. Try again."
      );
      setPhase("failed");
    }
  }

  // ---- The AI call takes a beat. Say so, and say what is happening. ----
  if (phase === "working") {
    return (
      <div className="app">
        <div className="spacer" />
        <div className="beat">
          <span className="spinner" />
          <div className="big">{COPY.checkIn.workingOnIt}</div>
          <p className="muted">Reading your update and writing questions about you. A few seconds.</p>
        </div>
        <div className="spacer" />
      </div>
    );
  }

  if (phase === "followup") {
    return (
      <div className="app">
        <div className="header">
          <h1>One more thing</h1>
        </div>
        <div className="card live">
          <span className="tag">To make these better</span>
          <p>{followupPrompt}</p>
        </div>
        <textarea
          className="input"
          value={followupText}
          onChange={(e) => setFollowupText(e.target.value)}
          placeholder="Type your answer…"
        />
        {err && <p className="error">{err}</p>}
        {/* These two used to be the same call with the same payload, and the
            primary was labelled "ADD SOMETHING ELSE", which described neither. */}
        <button className="btn primary" disabled={!followupText.trim()} onClick={submitFollowup}>
          SEND THIS TOO
        </button>
        <button className="link" onClick={submitFollowup}>Skip this and build my questions</button>
      </div>
    );
  }

  if (phase === "failed") {
    return (
      <div className="app">
        <div className="header">
          <h1>That didn&apos;t work</h1>
          <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>League home</button>
        </div>
        <div className="card">
          <p className="error">{err || COPY.checkIn.genFailed}</p>
          <p className="muted">Your update is still here. Nothing was lost.</p>
        </div>
        <button className="btn primary" onClick={() => { setErr(""); setPhase("compose"); }}>
          Back to my update
        </button>
      </div>
    );
  }

  // ---- compose ----
  const canSend = seasonState === "ready" && !!text.trim();

  return (
    <div className="app">
      <div className="header">
        <h1>{COPY.checkIn.headline}</h1>
        <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>League home</button>
      </div>
      <p className="muted">{COPY.checkIn.body}</p>

      {/* No season yet means there is nothing to attach an update to. Say that,
          instead of showing a button that quietly refuses to work. */}
      {seasonState === "over" && (
        <div className="card">
          <span className="tag">Season's done</span>
          <p>
            This season is finished, so a new update wouldn&apos;t make it into a drop. Go see how it
            all ended.
          </p>
          <button className="btn primary" onClick={() => navigate(`/league/${leagueId}`)}>See the final standings</button>
        </div>
      )}

      {seasonState === "none" && (
        <div className="card live">
          <span className="tag">Not quite yet</span>
          <p>
            Your commissioner hasn&apos;t set the season up yet, so there&apos;s nowhere for your update to
            go. Give it a minute and check back.
          </p>
          <button className="btn ghost" onClick={() => navigate(`/league/${leagueId}`)}>Back to league</button>
        </div>
      )}

      {(seasonState === "loading" || seasonState === "ready") && (
        <>
          <div className="card">
            <span className="tag">Some places to go</span>
            <ul className="recently">
              {COPY.checkIn.prompts.map((p) => <li key={p} className="muted">{p}</li>)}
            </ul>
          </div>

          <textarea
            className="input"
            style={{ minHeight: 220 }}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type your update here… (tip: tap the 🎤 on your keyboard to talk instead of type)"
          />

          {err && <p className="error">{err}</p>}
          <button className="btn primary" disabled={!canSend} onClick={submitInitial}>
            {seasonState === "loading" ? "Loading…" : "Send it"}
          </button>
          <p className="muted">{COPY.checkIn.footer}</p>
        </>
      )}
    </div>
  );
}
